import {
  type AnalyticsListRequest,
  type AnalyticsQueryRequest,
  crewMerchantApi,
  CrewMerchantApi,
  CrewMerchantUi,
  Prettify,
  ReportableEntity,
} from 'corso-types';

import { generatePath } from 'react-router-dom';
import { z } from 'zod';
import { getAccessToken } from './accessToken';
import env from './env';
import {
  ErrorResponse,
  isApiError,
  isErrorMessage,
} from './utils/errorResponse';
import { addBreadcrumb } from './utils/observability';
import { UnsafeContextError } from './utils/unsafeContextError';

export type ExchangeQuery = z.infer<
  (typeof crewMerchantApi)['/:storeId/product/exchange/:idFromPlatform']['get']['request']['query']
>;

/**
 * Type-safe API client for the Crew Merchant API.
 *
 * Architecture:
 * - `buildRequest`: Core request builder that handles URL construction, headers, params, and error handling
 * - `request`: Standard JSON API requests (uses buildRequest + parseResponse)
 * - `requestStream`: Streaming endpoints that return raw Response for SSE consumption (uses buildRequest only)
 */

type PathParams<Path extends CrewMerchantApi.ApiPath> = Parameters<
  typeof generatePath<Path>
>[1];

type ApiRequest = {
  [P in CrewMerchantApi.ApiPath]: {
    [M in CrewMerchantApi.MethodOfPath<P>]: Prettify<
      {
        path: P;
        method: M;
      } & (PathParams<P> extends undefined
        ? {
            params?: never;
          }
        : { params: PathParams<P> }) &
        (CrewMerchantApi.RequestBody<P, M> extends never
          ? {
              body?: never;
            }
          : {
              body: CrewMerchantApi.RequestBody<P, M>;
            }) &
        (CrewMerchantApi.RequestQuery<P, M> extends never
          ? {
              query?: never;
            }
          : {
              query: CrewMerchantApi.RequestQuery<P, M>;
            })
    >;
  }[CrewMerchantApi.MethodOfPath<P>];
}[CrewMerchantApi.ApiPath];

async function parseResponse<Data>(response: Response) {
  const clone = response.clone(); // clone the response to read the body if it's already been read
  const body: unknown = await clone.json(); // may throw SyntaxError if the body is not valid JSON
  if (response.ok) return body as Data; // trust the API we've defined

  // TODO fix error responses to be consistent from the API
  // TODO extend API error to support full Zod errors
  // ! only throw errors that are defined in the API to be handled by the createQuery
  if (isApiError(body))
    throw new ErrorResponse(body.clientMessage, response, body);
  if (isErrorMessage(body))
    throw new ErrorResponse(body.message, response, body);

  // TODO handle API errors more consistently
  throw new ErrorResponse(
    'Something went wrong, please try again later.',
    response,
    body,
  );
}

/**
 * Core request builder that constructs and executes API requests.
 * Returns the raw Response for custom handling (e.g., streaming).
 */
async function buildRequest<
  Path extends CrewMerchantApi.ApiPath,
  Method extends CrewMerchantApi.MethodOfPath<Path>,
>(
  baseUrl: string,
  {
    path,
    method,
    params,
    body,
    query,
  }: Extract<ApiRequest, { path: Path; method: Method }>,
  fetchInit?: Omit<RequestInit, 'method' | 'body'>,
) {
  const { headers: initHeaders, ...init } = fetchInit ?? {};

  // token-login is unauthenticated and is what the widget getter calls to
  // re-mint — attaching a bearer here would recurse through getAccessToken
  const accessToken =
    path === '/user/token/login' ? null : await getAccessToken();

  // TODO find a way to define required headers as part of the API definition
  const defaultHeaders = new Headers({
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  });

  // * this is gross, and alternatives should be considered; however, it encodes params so that application code doesn't have to
  const encodedParams = params
    ? (Object.fromEntries(
        Object.entries<
          NonNullable<PathParams<Path>>[keyof NonNullable<PathParams<Path>>]
        >(params).map(([key, value]) => [
          key,
          value ? encodeURIComponent(value) : null,
        ]),
      ) as PathParams<Path>)
    : undefined;

  const url = new URL(
    `${baseUrl}/merchant${generatePath(path, encodedParams)}`,
  );

  if (query) {
    Object.entries<unknown>(query).forEach(([key, value]) => {
      if (value === undefined) return; // omit undefined values from being added to the query string
      // oxlint-disable-next-line typescript/no-base-to-string
      url.searchParams.append(key, String(value)); // ! this coercion is not safe, but query params must always be interchanged as string values
    });
  }

  const jsonBody = body ? JSON.stringify(body) : undefined;

  const headers = new Headers({
    ...Object.fromEntries(defaultHeaders),
    ...Object.fromEntries(new Headers(initHeaders)),
  });

  const response = await fetch(url.toString(), {
    method,
    body: jsonBody,
    headers,
    ...init,
  }).catch((error: unknown) => {
    const errorContext = {
      buildSha: env.VITE_BUILD_SHA,
      method,
      url: url.toString(),
      body: jsonBody,
      headers,
      ...init,
    };

    addBreadcrumb({
      category: 'fetch',
      message: 'API Request Failed to Fetch',
      level: 'warning',
      data: errorContext,
    });
    // TODO evaluate why the native `cause` property is not being sent to or captured by Sentry

    if (error instanceof Error) {
      throw new UnsafeContextError(`API Request Failed to Fetch`, {
        cause: { error, errorContext },
      });
    }

    throw new UnsafeContextError('API Request Failed to Fetch with Non-Error', {
      cause: { error, errorContext },
    });
  });

  return response;
}

// Sidekick is served by the MCP server, so it gets its own client
function createClient(baseUrl: string) {
  /**
   * Standard API request that parses JSON response.
   */
  async function request<
    Path extends CrewMerchantApi.ApiPath,
    Method extends CrewMerchantApi.MethodOfPath<Path>,
  >(
    apiRequest: Extract<ApiRequest, { path: Path; method: Method }>,
    fetchInit?: Omit<RequestInit, 'method' | 'body'>,
  ) {
    const response = await buildRequest(baseUrl, apiRequest, fetchInit);
    return parseResponse<CrewMerchantApi.ResponseBody<Path, Method>>(response);
  }

  async function requestRaw<
    Path extends CrewMerchantApi.ApiPath,
    Method extends CrewMerchantApi.MethodOfPath<Path>,
  >(
    apiRequest: Extract<ApiRequest, { path: Path; method: Method }>,
    fetchInit?: Omit<RequestInit, 'method' | 'body'>,
  ) {
    const response = await buildRequest(baseUrl, apiRequest, fetchInit);

    if (!response.ok) {
      throw new ErrorResponse(
        `Stream request failed: ${response.status}`,
        response,
        null,
      );
    }

    if (!response.body) {
      throw new Error('No response body available for streaming');
    }

    return response;
  }

  return { request, requestRaw };
}

const { request } = createClient(env.VITE_BASE_API_URL);
const mcp = createClient(env.VITE_BASE_MCP_URL);

/** Abstractions upon HTTP requests with `fetch` with trust-based type-safety on backend types. */
export type UploadPath = CrewMerchantApi.RequestQuery<
  '/signed-url',
  'get'
>['path'];

const api = {
  merchantPaidProtectSet: () =>
    request({
      path: '/merchant-paid-protect-set',
      method: 'get',
      params: {},
    }),
  signedUrl: {
    get: (filename: string, path: UploadPath) =>
      request({
        path: '/signed-url',
        method: 'get',
        params: {}, // TODO determine why and adjust related types so it must be omitted
        query: {
          filename,
          path,
        },
      }),
  },
  // ? might better be elsewhere, since it doesn't match any paths provided by the api, breaking that pattern, and it further abstracts/orchestrates logic
  fileUpload: {
    put: async (file: File, path: UploadPath) => {
      const { signedUploadUrl, url, uploadFilename } = await api.signedUrl.get(
        file.name,
        path,
      );
      const response = await fetch(signedUploadUrl, {
        method: 'PUT',
        body: file,
        // Presigned URLs must have this header set when using AWS S3 client on DigitalOcean @see https://stackoverflow.com/questions/66555915/cannot-upload-files-with-acl-public-read-to-digital-ocean-spaces
        headers: {
          'x-amz-acl': 'public-read',
        },
      });
      if (response.ok) return { name: uploadFilename, src: url };
      throw new Error(`Error Uploading File: ${file.name}`, {
        cause: response.statusText,
      });
    },
  },
  user: {
    get: (idFromPlatform: string) =>
      request({
        path: '/user/:idFromPlatform',
        method: 'get',
        params: { idFromPlatform },
      }),
    tokenLogin: (
      query: CrewMerchantApi.RequestQuery<'/user/token/login', 'get'>,
    ) =>
      request({
        path: '/user/token/login',
        method: 'get',
        params: {},
        query,
      }),
    getWithAccessCode: (accessCode: string) =>
      request({
        path: '/users',
        method: 'get',
        params: {},
        query: { accessCode },
      }),
    mcpIdentityGrant: {
      list: (idFromPlatform: string) =>
        request({
          path: '/user/:idFromPlatform/mcp-identity-grant',
          method: 'get',
          params: { idFromPlatform },
        }),
      delete: (idFromPlatform: string, identityGrantId: string) =>
        request({
          path: '/user/:idFromPlatform/mcp-identity-grant/:identityGrantId',
          method: 'delete',
          params: { idFromPlatform, identityGrantId },
        }),
    },
  },

  // platform wide, unbounded by store — the API gates it on Corso org membership
  conciergeSearch: (
    body: CrewMerchantApi.RequestBody<'/concierge/search', 'post'>,
  ) =>
    request({
      path: '/concierge/search',
      method: 'post',
      params: {},
      body,
    }),
  conciergeCustomer: ({ customerEmail }: { customerEmail: string }) =>
    request({
      path: '/concierge/customer',
      method: 'get',
      params: {},
      query: { email: customerEmail },
    }),
  store: (storeId: `${number}`) => ({
    conversations: (userId: string) => ({
      list: () =>
        mcp.request({
          path: '/:storeId/user/:userId/conversations',
          method: 'get',
          params: { storeId, userId },
        }),
      create: () =>
        mcp.request({
          path: '/:storeId/user/:userId/conversations',
          method: 'post',
          params: { storeId, userId },
        }),
      get: (id: string) =>
        mcp.request({
          path: '/:storeId/user/:userId/conversations/:conversationId',
          method: 'get',
          params: { storeId, userId, conversationId: id },
        }),
      rename: (id: string, name: string) =>
        mcp.request({
          path: '/:storeId/user/:userId/conversations/:conversationId',
          method: 'put',
          params: { storeId, userId, conversationId: id },
          body: { name },
        }),
      feedback: (
        conversationId: string,
        messageId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/user/:userId/conversations/:conversationId/message/:messageId',
          'post'
        >,
      ) =>
        mcp.request({
          path: '/:storeId/user/:userId/conversations/:conversationId/message/:messageId',
          method: 'post',
          params: { storeId, userId, conversationId, messageId },
          body,
        }),
      stream: (
        conversationId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/user/:userId/conversations/:conversationId',
          'post'
        >,
        signal?: AbortSignal,
      ) =>
        mcp.requestRaw(
          {
            path: '/:storeId/user/:userId/conversations/:conversationId',
            method: 'post',
            params: { storeId, userId, conversationId },
            body,
          },
          { signal },
        ),
    }),

    userAlerts: (userId: string) => ({
      list: () =>
        request({
          path: '/:storeId/user/:userId/alert',
          method: 'get',
          params: { storeId, userId },
        }),

      markAllRead: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/user/:userId/alert',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/user/:userId/alert',
          method: 'put',
          params: { storeId, userId },
          body,
        }),

      toggleRead: (
        alertId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/user/:userId/alert/:alertId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/user/:userId/alert/:alertId',
          method: 'put',
          params: { storeId, userId, alertId },
          body,
        }),
    }),

    search: (body: CrewMerchantApi.RequestBody<'/:storeId/search', 'post'>) =>
      request({
        path: '/:storeId/search',
        method: 'post',
        params: { storeId },
        body,
      }),

    searchViews: {
      listViews: () =>
        request({
          path: '/:storeId/view',
          method: 'get',
          params: { storeId },
        }),
      createView: (
        body: CrewMerchantApi.RequestBody<'/:storeId/view', 'post'>,
      ) =>
        request({
          path: '/:storeId/view',
          method: 'post',
          params: { storeId },
          body,
        }),
      getView: (viewId: string) =>
        request({
          path: '/:storeId/view/:viewId',
          method: 'get',
          params: { storeId, viewId },
        }),
      updateView: (
        body: CrewMerchantApi.RequestBody<'/:storeId/view/:viewId', 'put'>,
      ) =>
        request({
          path: '/:storeId/view/:viewId',
          method: 'put',
          params: { storeId, viewId: body.id.toString() },
          body,
        }),
      deleteView: (id: number) =>
        request({
          path: '/:storeId/view/:viewId',
          method: 'delete',
          params: { storeId, viewId: `${id}` },
        }),
    },

    reports: {
      runInline: (
        body: CrewMerchantApi.RequestBody<'/:storeId/reports/run', 'post'>,
      ) =>
        request({
          path: '/:storeId/reports/run',
          method: 'post',
          params: { storeId },
          body,
        }),
      runFromDefinition: (definitionId: number) =>
        request({
          path: '/:storeId/reports/definitions/:definitionId/run',
          method: 'post',
          params: { storeId, definitionId: `${definitionId}` },
        }),
      definitions: {
        list: (entity: ReportableEntity) =>
          request({
            path: '/:storeId/reports/:entity/definitions',
            method: 'get',
            params: { storeId, entity },
          }),
        create: (
          entity: ReportableEntity,
          body: CrewMerchantApi.RequestBody<
            '/:storeId/reports/:entity/definitions',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/reports/:entity/definitions',
            method: 'post',
            params: { storeId, entity },
            body,
          }),
        get: (entity: ReportableEntity, definitionId: string) =>
          request({
            path: '/:storeId/reports/:entity/definitions/:definitionId',
            method: 'get',
            params: { storeId, entity, definitionId },
          }),
        update: (
          entity: ReportableEntity,
          definitionId: string,
          body: CrewMerchantApi.RequestBody<
            '/:storeId/reports/:entity/definitions/:definitionId',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/reports/:entity/definitions/:definitionId',
            method: 'put',
            params: { storeId, entity, definitionId },
            body,
          }),
        delete: (entity: ReportableEntity, definitionId: string) =>
          request({
            path: '/:storeId/reports/:entity/definitions/:definitionId',
            method: 'delete',
            params: { storeId, entity, definitionId },
          }),
      },
    },

    frontReport: () =>
      request({
        path: '/:storeId/report/front',
        method: 'get',
        params: { storeId },
      }),

    discountDefinitions: {
      list: () =>
        request({
          path: '/:storeId/discount-definition',
          method: 'get',
          params: { storeId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/discount-definition',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/discount-definition',
          method: 'post',
          params: { storeId },
          body,
        }),
      delete: (storeDiscountDefinitionId: number) =>
        request({
          path: '/:storeId/discount-definition/:storeDiscountDefinitionId',
          method: 'delete',
          params: {
            storeId,
            storeDiscountDefinitionId: storeDiscountDefinitionId.toString(),
          },
        }),
    },

    invoices: {
      list: () =>
        request({
          path: '/:storeId/invoice',
          method: 'get',
          params: { storeId },
        }),
    },

    customer: {
      get: ({ customerEmail }: { customerEmail: string }) =>
        request({
          path: '/:storeId/customer',
          method: 'get',
          params: { storeId },
          query: { email: customerEmail },
        }),
    },

    registrations: {
      get: ({ registrationId }: { registrationId: string }) =>
        request({
          path: '/:storeId/registration/:registrationId',
          method: 'get',
          params: { storeId, registrationId },
        }),

      cancel: ({ registrationId }: { registrationId: string }) =>
        request({
          path: '/:storeId/registration/:registrationId',
          method: 'delete',
          params: { storeId, registrationId },
        }),

      restore: ({ registrationId }: { registrationId: string }) =>
        request({
          path: '/:storeId/registration/:registrationId/restore',
          method: 'post',
          params: { storeId, registrationId },
        }),

      update: ({
        registrationId,
        body,
      }: {
        registrationId: string;
        body: CrewMerchantUi.RegistrationUpdate;
      }) =>
        request({
          path: '/:storeId/registration/:registrationId',
          method: 'put',
          params: { storeId, registrationId },
          body,
        }),

      tags: ({ registrationId }: { registrationId: string }) => ({
        list: () =>
          request({
            path: '/:storeId/registration/:registrationId/tags',
            method: 'get',
            params: { storeId, registrationId },
          }),
        upsert: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/registration/:registrationId/tags',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/registration/:registrationId/tags',
            method: 'put',
            params: { storeId, registrationId },
            body,
          }),
      }),

      addTimelineEvent: ({
        registrationId,
        body,
      }: {
        registrationId: string;
        body: CrewMerchantApi.RequestBody<
          '/:storeId/registration/:registrationId/timeline',
          'post'
        >;
      }) =>
        request({
          path: '/:storeId/registration/:registrationId/timeline',
          method: 'post',
          params: { storeId, registrationId },
          body,
        }),
    },
    storeRegistrationTags: {
      list: () =>
        request({
          path: '/:storeId/registration/tags',
          method: 'get',
          params: { storeId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/registration/tags',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/registration/tags',
          method: 'post',
          params: { storeId },
          body,
        }),
      delete: (tagId: string) =>
        request({
          path: '/:storeId/registration/tags/:tagId',
          method: 'delete',
          params: { storeId, tagId },
        }),
    },
    shippingProtection: {
      checkoutPlusEmbed: () =>
        request({
          path: '/:storeId/shipping-protection/checkout-plus-embed',
          method: 'get',
          params: { storeId },
        }),
      protectionRates: () =>
        request({
          path: '/:storeId/shipping-protection/rate',
          method: 'get',
          params: { storeId },
        }),
      createProtectionRate: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/shipping-protection/rate',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/shipping-protection/rate',
          method: 'post',
          params: { storeId },
          body,
        }),

      updateProtectionRate: (
        rateId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/shipping-protection/rate/:rateId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/shipping-protection/rate/:rateId',
          method: 'put',
          params: { storeId, rateId },
          body,
        }),

      deleteProtectionRate: (rateId: string) =>
        request({
          path: '/:storeId/shipping-protection/rate/:rateId',
          method: 'delete',
          params: { storeId, rateId },
        }),

      configure: (method: CrewMerchantUi.ConfigShippingMethod) =>
        request({
          path: '/:storeId/shipping-protection/configure',
          method: 'post',
          params: { storeId },
          body: { method },
        }),
      claimCreate: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/shipping-protection/claim',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/shipping-protection/claim',
          method: 'post',
          params: { storeId },
          body,
        }),
      claim: (shippingClaimId: string) => ({
        tags: {
          list: () =>
            request({
              path: '/:storeId/shipping-protection/claim/:shippingClaimId/tags',
              method: 'get',
              params: { storeId, shippingClaimId },
            }),
          upsert: (
            body: CrewMerchantApi.RequestBody<
              '/:storeId/shipping-protection/claim/:shippingClaimId/tags',
              'put'
            >,
          ) =>
            request({
              path: '/:storeId/shipping-protection/claim/:shippingClaimId/tags',
              method: 'put',
              params: { storeId, shippingClaimId },
              body,
            }),
        },

        get: () =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId',
            method: 'get',
            params: { storeId, shippingClaimId },
          }),

        messages: () =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/messages',
            method: 'get',
            params: { storeId, shippingClaimId },
          }),

        finalize: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/finalize',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/finalize',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),

        close: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/close',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/close',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),

        manualRefund: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/manual-refund',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/manual-refund',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),

        manualReorder: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/manual-reorder',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/manual-reorder',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),

        refundShipping: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/refund-shipping',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/refund-shipping',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),

        restore: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/restore',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/restore',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),

        update: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId',
            method: 'put',
            params: { storeId, shippingClaimId },
            body,
          }),

        updateCustomerInfo: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/customer-info',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/customer-info',
            method: 'put',
            params: { storeId, shippingClaimId },
            body,
          }),

        lineItem: {
          add: (
            body: CrewMerchantApi.RequestBody<
              '/:storeId/shipping-protection/claim/:shippingClaimId/line-item',
              'post'
            >,
          ) =>
            request({
              path: '/:storeId/shipping-protection/claim/:shippingClaimId/line-item',
              method: 'post',
              params: { storeId, shippingClaimId },
              body,
            }),

          delete: (shippingClaimLineItemId: string) =>
            request({
              path: '/:storeId/shipping-protection/claim/:shippingClaimId/line-item/:shippingClaimLineItemId',
              method: 'delete',
              params: { storeId, shippingClaimId, shippingClaimLineItemId },
            }),
        },

        replacementItem: {
          delete: (replacementItemId: string) =>
            request({
              path: '/:storeId/shipping-protection/claim/:shippingClaimId/replacement-item/:replacementItemId',
              method: 'delete',
              params: { storeId, shippingClaimId, replacementItemId },
            }),
        },

        inventoryLevels: () =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/inventory-levels',
            method: 'get',
            params: { storeId, shippingClaimId },
          }),

        risk: () =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/risk',
            method: 'get',
            params: { storeId, shippingClaimId },
          }),

        lookup: () =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/lookup',
            method: 'get',
            params: { storeId, shippingClaimId },
          }),

        addTimelineEvent: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/shipping-protection/claim/:shippingClaimId/timeline',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/shipping-protection/claim/:shippingClaimId/timeline',
            method: 'post',
            params: { storeId, shippingClaimId },
            body,
          }),
      }),
    },
    analytics: {
      query: (body: AnalyticsQueryRequest) =>
        request({
          path: '/:storeId/analytics/query',
          method: 'post',
          params: { storeId },
          body,
        }),
      lists: {
        query: (body: AnalyticsListRequest) =>
          request({
            path: '/:storeId/analytics/lists/query',
            method: 'post',
            params: { storeId },
            body,
          }),
      },
    },
    order: {
      get: (query: CrewMerchantApi.RequestQuery<'/:storeId/order', 'get'>) =>
        request({
          path: '/:storeId/order',
          method: 'get',
          params: { storeId },
          query,
        }),
      tracking: (storeOrderId: string) =>
        request({
          path: '/:storeId/order/:storeOrderId/tracking',
          method: 'get',
          params: { storeId, storeOrderId },
        }),
      updateLineItem: (
        storeOrderId: string,
        lineItemId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/order/:storeOrderId/line-item/:lineItemId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/order/:storeOrderId/line-item/:lineItemId',
          method: 'put',
          params: { storeId, storeOrderId, lineItemId },
          body,
        }),
    },
    claimCreate: (
      claimCreate: CrewMerchantApi.RequestBody<'/:storeId/claim', 'post'>,
    ) =>
      request({
        path: '/:storeId/claim',
        method: 'post',
        params: { storeId },
        body: claimCreate,
      }),
    claim: (claimId: string) => ({
      get: () =>
        request({
          path: '/:storeId/claim/:claimId',
          method: 'get',
          params: { storeId, claimId },
        }),

      risk: () =>
        request({
          path: '/:storeId/claim/:claimId/risk',
          method: 'get',
          params: { storeId, claimId },
        }),

      orderLineage: () =>
        request({
          path: '/:storeId/claim/:claimId/order-lineage',
          method: 'get',
          params: { storeId, claimId },
        }),

      reopen: () =>
        request({
          path: '/:storeId/claim/:claimId/reopen',
          method: 'post',
          params: { storeId, claimId },
        }),

      finalize: (
        claimFinalize: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/finalize',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/finalize',
          method: 'post',
          body: claimFinalize,
          params: { storeId, claimId },
        }),

      ledger: () =>
        request({
          path: '/:storeId/claim/:claimId/ledger',
          method: 'get',
          params: { storeId, claimId },
        }),

      quoteAndCreateShipment: (
        shipmentCreate: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/shipment/quote-and-create',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/shipment/quote-and-create',
          method: 'post',
          body: shipmentCreate,
          params: { storeId, claimId },
        }),

      createShipment: (
        shipmentCreate: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/shipment',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/shipment',
          method: 'post',
          body: shipmentCreate,
          params: { storeId, claimId },
        }),
      createShipmentQuote: (
        shipmentQuoteCreate: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/shipment/quote',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/shipment/quote',
          method: 'post',
          body: shipmentQuoteCreate,
          params: { storeId, claimId },
        }),
      cancel: () =>
        request({
          path: '/:storeId/claim/:claimId/cancel',
          method: 'post',

          params: { storeId, claimId },
        }),
      requestInfo: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/request-info',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/request-info',
          method: 'post',
          params: { storeId, claimId },
          body,
        }),
      updateCustomerInfo: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/customer-info',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/customer-info',
          method: 'put',
          params: { storeId, claimId },
          body,
        }),

      updateFees: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/fees',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/fees',
          method: 'put',
          params: { storeId, claimId },
          body,
        }),
      addTimelineEvent: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/claim/:claimId/timeline',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/claim/:claimId/timeline',
          method: 'post',
          params: { storeId, claimId },
          body,
        }),
      tags: {
        list: async () =>
          request({
            path: '/:storeId/claim/:claimId/tags',
            method: 'get',
            params: { storeId, claimId },
          }),
        upsert: async (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/claim/:claimId/tags',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/claim/:claimId/tags',
            method: 'put',
            params: { storeId, claimId },
            body,
          }),
      },
      lineItem: (claimLineItemId: string) => ({
        update: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/claim/:claimId/line-item/:claimLineItemId',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/claim/:claimId/line-item/:claimLineItemId',
            method: 'put',
            params: { storeId, claimId, claimLineItemId },
            body,
          }),
        fee: (claimLineItemFeeId: string) => ({
          update: (
            body: CrewMerchantApi.RequestBody<
              '/:storeId/claim/:claimId/line-item/:claimLineItemId/fee/:claimLineItemFeeId',
              'put'
            >,
          ) =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/fee/:claimLineItemFeeId',
              method: 'put',
              params: { storeId, claimId, claimLineItemId, claimLineItemFeeId },
              body,
            }),
          remove: () =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/fee/:claimLineItemFeeId',
              method: 'delete',
              params: { storeId, claimId, claimLineItemId, claimLineItemFeeId },
            }),
        }),
        media: {
          create: (
            body: CrewMerchantApi.RequestBody<
              '/:storeId/claim/:claimId/line-item/:claimLineItemId/media',
              'post'
            >,
          ) =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/media',
              method: 'post',
              params: { storeId, claimId, claimLineItemId },
              body,
            }),
          remove: (claimLineItemImageId: string) =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/media/:claimLineItemImageId',
              method: 'delete',
              params: {
                storeId,
                claimId,
                claimLineItemId,
                claimLineItemImageId,
              },
            }),
        },
        inspection: {
          create: (
            body: CrewMerchantApi.RequestBody<
              '/:storeId/claim/:claimId/line-item/:claimLineItemId/inspection',
              'post'
            >,
          ) =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/inspection',
              method: 'post',
              params: { storeId, claimId, claimLineItemId },
              body,
            }),
          delete: (claimInspectionId: string) =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/inspection/:claimInspectionId',
              method: 'delete',
              params: { storeId, claimId, claimLineItemId, claimInspectionId },
            }),

          settings: () =>
            request({
              path: '/:storeId/claim/:claimId/line-item/:claimLineItemId/inspection/settings',
              method: 'get',
              params: { storeId, claimId, claimLineItemId },
            }),
        },
      }),
    }),

    identityGrant: {
      create: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/settings/identity-grant',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/settings/identity-grant',
          method: 'post',
          params: { storeId },
          body,
        }),
      list: () =>
        request({
          path: '/:storeId/settings/identity-grant',
          method: 'get',
          params: { storeId },
        }),
      delete: (identityGrantId: string) =>
        request({
          path: '/:storeId/settings/identity-grant/:identityGrantId',
          method: 'delete',
          params: { storeId, identityGrantId },
        }),
    },
    claimReason: {
      get: () =>
        request({
          path: '/:storeId/claim-reason',
          method: 'get',
          params: { storeId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<'/:storeId/claim-reason', 'post'>,
      ) =>
        request({
          path: '/:storeId/claim-reason',
          method: 'post',
          params: { storeId },
          body,
        }),
      update: (
        claimReasonId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/claim-reason/:claimReasonId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/claim-reason/:claimReasonId',
          method: 'put',
          params: { storeId, claimReasonId },
          body,
        }),
      groups: {
        get: () =>
          request({
            path: '/:storeId/claim-reason/group',
            method: 'get',
            params: { storeId },
          }),
        update: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/claim-reason/group/:claimReasonGroupId',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/claim-reason/group/:claimReasonGroupId',
            method: 'put',
            params: { storeId, claimReasonGroupId: String(body.id) },
            body,
          }),
        create: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/claim-reason/group',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/claim-reason/group',
            method: 'post',
            params: { storeId },
            body,
          }),
        delete: (claimReasonGroupId: string) =>
          request({
            path: '/:storeId/claim-reason/group/:claimReasonGroupId',
            method: 'delete',
            params: { storeId, claimReasonGroupId },
          }),
      },
    },

    storeUser: {
      get: () =>
        request({
          path: '/:storeId/user',
          method: 'get',
          params: { storeId },
        }),
      signUp: (
        body: CrewMerchantApi.RequestBody<'/:storeId/user/sign-up', 'post'>,
        accessCode: string,
      ) =>
        request({
          path: '/:storeId/user/sign-up',
          method: 'post',
          params: { storeId },
          query: { accessCode },
          body,
        }),
      create: (body: CrewMerchantApi.RequestBody<'/:storeId/user', 'post'>) =>
        request({
          path: '/:storeId/user',
          method: 'post',
          params: { storeId },
          body,
        }),

      update: (
        userId: string,
        body: CrewMerchantApi.RequestBody<'/:storeId/user/:userId', 'put'>,
      ) =>
        request({
          path: '/:storeId/user/:userId',
          method: 'put',
          params: { storeId, userId },
          body,
        }),

      delete: (userId: string) =>
        request({
          path: '/:storeId/user/:userId',
          method: 'delete',
          params: { storeId, userId },
        }),

      resetPassword: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/user/:userId/reset-password',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/user/:userId/reset-password',
          method: 'post',
          params: { storeId, userId: body.id.toString() },
          body,
        }),
    },
    payment: {
      refund: (customerPaymentId: string) =>
        request({
          path: '/:storeId/customer-payment/:customerPaymentId/refund',
          method: 'post',
          params: { storeId, customerPaymentId },
        }),
    },
    settings: {
      config: {
        get: () =>
          request({
            path: '/:storeId/settings/config',
            method: 'get',
            params: { storeId },
          }),
        put: (
          body: CrewMerchantApi.RequestBody<'/:storeId/settings/config', 'put'>,
        ) =>
          request({
            path: '/:storeId/settings/config',
            method: 'put',
            params: { storeId },
            body,
          }),
      },
      integrations: {
        get: () =>
          request({
            path: '/:storeId/settings/integrations',
            method: 'get',
            params: { storeId },
          }),
        update: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/settings/integrations',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/settings/integrations',
            method: 'put',
            params: { storeId },
            body,
          }),
        refreshShippingCarriers: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/settings/integrations/shipping-provider/refresh',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/settings/integrations/shipping-provider/refresh',
            method: 'post',
            params: { storeId },
            body,
          }),
        disconnectStripe: () =>
          request({
            path: '/:storeId/settings/integrations/stripe',
            method: 'delete',
            params: { storeId },
          }),
        testKlaviyoEvents: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/settings/integrations/klaviyo/test',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/settings/integrations/klaviyo/test',
            method: 'post',
            params: { storeId },
            body,
          }),
      },
      webhooks: {
        list: () =>
          request({
            path: '/:storeId/settings/webhooks',
            method: 'get',
            params: { storeId },
          }),
        create: (
          body: CrewMerchantApi.RequestBody<
            '/:storeId/settings/webhooks',
            'post'
          >,
        ) =>
          request({
            path: '/:storeId/settings/webhooks',
            method: 'post',
            params: { storeId },
            body,
          }),
        get: (webhookId: string) =>
          request({
            path: '/:storeId/settings/webhooks/:webhookId',
            method: 'get',
            params: { storeId, webhookId },
          }),
        update: (
          webhookId: string,
          body: CrewMerchantApi.RequestBody<
            '/:storeId/settings/webhooks/:webhookId',
            'put'
          >,
        ) =>
          request({
            path: '/:storeId/settings/webhooks/:webhookId',
            method: 'put',
            params: { storeId, webhookId },
            body,
          }),
        delete: (webhookId: string) =>
          request({
            path: '/:storeId/settings/webhooks/:webhookId',
            method: 'delete',
            params: { storeId, webhookId },
          }),
        deliveries: {
          search: (
            webhookId: string,
            body: CrewMerchantApi.RequestBody<
              '/:storeId/settings/webhooks/:webhookId/deliveries/search',
              'post'
            >,
          ) =>
            request({
              path: '/:storeId/settings/webhooks/:webhookId/deliveries/search',
              method: 'post',
              params: { storeId, webhookId },
              body,
            }),
          retry: (webhookId: string, deliveryId: string) =>
            request({
              path: '/:storeId/settings/webhooks/:webhookId/deliveries/:deliveryId/retry',
              method: 'post',
              params: { storeId, webhookId, deliveryId },
            }),
        },
        events: {
          update: (
            webhookId: string,
            eventId: string,
            body: CrewMerchantApi.RequestBody<
              '/:storeId/settings/webhooks/:webhookId/events/:eventId',
              'put'
            >,
          ) =>
            request({
              path: '/:storeId/settings/webhooks/:webhookId/events/:eventId',
              method: 'put',
              params: { storeId, webhookId, eventId },
              body,
            }),
        },
      },
      theme: {
        get: () =>
          request({
            path: '/:storeId/settings/theme',
            method: 'get',
            params: { storeId },
          }),
        update: (
          body: CrewMerchantApi.RequestBody<'/:storeId/settings/theme', 'put'>,
        ) =>
          request({
            path: '/:storeId/settings/theme',
            method: 'put',
            params: { storeId },
            body,
          }),
      },
    },
    factValues: {
      get: () =>
        request({
          path: '/:storeId/fact/values',
          method: 'get',
          params: { storeId },
        }),
    },
    storeShippingClaimTags: {
      list: () =>
        request({
          path: '/:storeId/shipping-protection/claim/tags',
          method: 'get',
          params: { storeId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<
          '/:storeId/shipping-protection/claim/tags',
          'post'
        >,
      ) =>
        request({
          path: '/:storeId/shipping-protection/claim/tags',
          method: 'post',
          params: { storeId },
          body,
        }),
      delete: (tagId: string) =>
        request({
          path: '/:storeId/shipping-protection/claim/tags/:tagId',
          method: 'delete',
          params: { storeId, tagId },
        }),
    },
    storeClaimTags: {
      list: () =>
        request({
          path: '/:storeId/claim/tags',
          method: 'get',
          params: { storeId },
        }),
      create: (body: { name: string }) =>
        request({
          path: '/:storeId/claim/tags',
          method: 'post',
          params: { storeId },
          body,
        }),
      delete: (tagId: string) =>
        request({
          path: '/:storeId/claim/tags/:tagId',
          method: 'delete',
          params: { storeId, tagId },
        }),
    },
    automations: {
      list: () =>
        request({
          path: '/:storeId/automation',
          method: 'get',
          params: { storeId },
        }),

      get: (automationId: string) =>
        request({
          path: '/:storeId/automation/:automationId',
          method: 'get',
          params: { storeId, automationId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<'/:storeId/automation', 'post'>,
      ) =>
        request({
          path: '/:storeId/automation',
          method: 'post',
          params: { storeId },
          body,
        }),
      update: (
        automationId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/automation/:automationId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/automation/:automationId',
          method: 'put',
          params: { storeId, automationId },
          body,
        }),
      delete: (automationId: string) =>
        request({
          path: '/:storeId/automation/:automationId',
          method: 'delete',
          params: { storeId, automationId },
        }),
    },
    returnLocation: {
      get: () =>
        request({
          path: '/:storeId/return-location',
          method: 'get',
          params: { storeId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<'/:storeId/return-location', 'post'>,
      ) =>
        request({
          path: '/:storeId/return-location',
          method: 'post',
          params: { storeId },
          body,
        }),
      update: (
        returnLocationId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/return-location/:returnLocationId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/return-location/:returnLocationId',
          method: 'put',
          params: { storeId, returnLocationId },
          body,
        }),
      delete: (returnLocationId: string) =>
        request({
          path: '/:storeId/return-location/:returnLocationId',
          method: 'delete',
          params: { storeId, returnLocationId },
        }),
    },

    verifyAddress: (
      body: CrewMerchantApi.RequestBody<'/:storeId/verify-address', 'post'>,
    ) =>
      request({
        path: '/:storeId/verify-address',
        method: 'post',
        params: { storeId },
        body,
      }),

    fulfillmentLocations: {
      get: () =>
        request({
          path: '/:storeId/fulfillment-location',
          method: 'get',
          params: { storeId },
        }),
    },

    // ? maybe move into the product object
    variantExchangeOptions: (idFromPlatform: string, query: ExchangeQuery) =>
      request({
        path: '/:storeId/product/exchange/:idFromPlatform',
        method: 'get',
        params: { storeId, idFromPlatform },
        query,
      }),
    product: {
      get: ({ storeProductId }: { storeProductId: string }) =>
        request({
          path: '/:storeId/product/:storeProductId',
          method: 'get',
          params: { storeId, storeProductId },
        }),

      update: (
        storeProductId: string,
        body: CrewMerchantUi.StoreProductUpdate,
      ) =>
        request({
          path: '/:storeId/product/:storeProductId',
          method: 'put',
          params: { storeId, storeProductId },
          body,
        }),

      delete: (storeProductId: string) =>
        request({
          path: '/:storeId/product/:storeProductId',
          method: 'delete',
          params: { storeId, storeProductId },
        }),

      groups: (storeProductId: string) =>
        request({
          path: '/:storeId/product/:storeProductId/group',
          method: 'get',
          params: { storeId, storeProductId },
        }),

      registrationChannels: (storeProductId: string) =>
        request({
          path: '/:storeId/product/:storeProductId/registration-channel',
          method: 'get',
          params: { storeId, storeProductId },
        }),

      search: (
        query: CrewMerchantApi.RequestQuery<'/:storeId/product', 'get'>,
      ) =>
        request({
          path: '/:storeId/product',
          method: 'get',
          params: { storeId },
          query,
        }),

      types: () =>
        request({
          path: '/:storeId/product/type',
          method: 'get',
          params: { storeId },
        }),

      sync: () =>
        request({
          path: '/:storeId/product/sync',
          method: 'post',
          params: { storeId },
        }),

      import: (body: CrewMerchantUi.StoreProductImport) =>
        request({
          path: '/:storeId/product/import',
          method: 'post',
          params: { storeId },
          body,
        }),

      tags: () =>
        request({
          path: '/:storeId/product/tag',
          method: 'get',
          params: { storeId },
        }),
      collections: () =>
        request({
          path: '/:storeId/product/collection',
          method: 'get',
          params: { storeId },
        }),
    },
    productGroup: {
      get: () =>
        request({
          path: '/:storeId/product/group',
          method: 'get',
          params: { storeId },
        }),
      update: (
        groupId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/product/group/:groupId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/product/group/:groupId',
          method: 'put',
          params: { storeId, groupId },
          body,
        }),
      delete: (groupId: string) =>
        request({
          path: '/:storeId/product/group/:groupId',
          method: 'delete',
          params: { storeId, groupId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<'/:storeId/product/group', 'post'>,
      ) =>
        request({
          path: '/:storeId/product/group',
          method: 'post',
          params: { storeId },
          body,
        }),

      listProducts: (groupId: string) =>
        request({
          path: '/:storeId/product/group/:groupId/list-products',
          method: 'get',
          params: { storeId, groupId },
        }),

      refreshProduct: (groupId: string) =>
        request({
          path: '/:storeId/product/group/:groupId/refresh-products',
          method: 'post',
          params: { storeId, groupId },
        }),
    },
    storeMetafields: {
      get: () =>
        request({
          path: '/:storeId/metafield',
          method: 'get',
          params: { storeId },
        }),
      platform: () =>
        request({
          path: '/:storeId/metafield/platform',
          method: 'get',
          params: { storeId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<'/:storeId/metafield', 'post'>,
      ) =>
        request({
          path: '/:storeId/metafield',
          method: 'post',
          params: { storeId },
          body,
        }),
      delete: (metafieldId: string) =>
        request({
          path: '/:storeId/metafield/:metafieldId',
          method: 'delete',
          params: { storeId, metafieldId },
        }),
    },
    customFields: {
      get: () =>
        request({
          path: '/:storeId/custom-field',
          method: 'get',
          params: { storeId },
        }),
      update: (
        customFieldId: string,
        body: CrewMerchantApi.RequestBody<
          '/:storeId/custom-field/:customFieldId',
          'put'
        >,
      ) =>
        request({
          path: '/:storeId/custom-field/:customFieldId',
          method: 'put',
          params: { storeId, customFieldId },
          body,
        }),
      delete: (customFieldId: string) =>
        request({
          path: '/:storeId/custom-field/:customFieldId',
          method: 'delete',
          params: { storeId, customFieldId },
        }),
      create: (
        body: CrewMerchantApi.RequestBody<'/:storeId/custom-field', 'post'>,
      ) =>
        request({
          path: '/:storeId/custom-field',
          method: 'post',
          params: { storeId },
          body,
        }),
    },
  }),
  organization: (organizationId: string) => ({
    analytics: {
      currencies: () =>
        request({
          path: '/organization/:organizationId/analytics/currencies',
          method: 'get',
          params: { organizationId },
        }),
      query: (body: AnalyticsQueryRequest, currency: string) =>
        request({
          path: '/organization/:organizationId/analytics/query',
          method: 'post',
          params: { organizationId },
          query: { currency },
          body,
        }),
      lists: {
        query: (body: AnalyticsListRequest, currency: string) =>
          request({
            path: '/organization/:organizationId/analytics/lists/query',
            method: 'post',
            params: { organizationId },
            query: { currency },
            body,
          }),
      },
    },
  }),
};

export default api;
